// Code pour la page de fin de jeu

// React imports
import { useParams, useNavigate } from 'react-router-dom';

// Components
import Banner from '../components/Banner';
import BackToModesButton from '../components/BackToModesButton.tsx';
import Button from '../components/Button';

// Contextes
import { useInTheGame } from '../utils/InTheGameContexte.tsx';
import { useGame } from '../utils/GameContexte.tsx';

// Librairies
import ReactMarkdown from 'react-markdown';
import { useEffect } from 'react';
import gsap from 'gsap';

function Fin() {
    const { id } = useParams();
    const title =
        id === 'entrainement' ? 'Entraînement' : id === 'expert' ? 'Mode expert' : 'Mode classique';
    const navigate = useNavigate();

    // données du jeu
    const {
        totalPoints,
        setGameFinished,
        totalQuestions,
        totalPlayers,
        equipesNoms,
        winner,
        equipesPoints,
    } = useGame();

    // Animation de fin
    useEffect(() => {
        const finTitle = document.querySelector('.finContainer__text--upper');
        const finParagraph = document.querySelector('.finContainer__text');
        const finParagraph2 = document.querySelector('.finText--spacer');
        const finTotalPoints = document.querySelector('.finTotalPoints');
        const finButtons = document.querySelector('.finButtons');

        const finResults = document.querySelector('.finContainer__results');
        const finWinner = document.querySelector('.winner');

        const tl = gsap.timeline();
        if (finTitle) {
            tl.fromTo(
                finTitle,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    ease: 'power3.out',
                }
            );
        }

        if (finParagraph) {
            tl.fromTo(
                finParagraph,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power3.out',
                }
            );
        }

        if (finParagraph2) {
            tl.fromTo(
                finParagraph2,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    ease: 'power3.out',
                }
            );
        }

        if (finTotalPoints) {
            tl.fromTo(
                finTotalPoints,
                { opacity: 0, scale: 0 },
                {
                    opacity: 1,
                    scale: 1,
                    duration: 0.5,
                    ease: 'power3.out',
                }
            );
        }

        if (finResults) {
            tl.fromTo(
                finResults,
                { opacity: 0, scale: 0 },
                {
                    opacity: 1,
                    scale: 1,
                    duration: 0.5,
                    ease: 'power3.out',
                }
            );
        }
        if (finWinner) {
            tl.fromTo(
                finWinner,
                { scale: 1 },
                {
                    scale: 1.3,
                    duration: 0.4,
                    ease: 'power3.out',
                    repeat: 1,
                    yoyo: true,
                }
            ),
                '<';
        }

        if (finButtons) {
            tl.fromTo(
                finButtons,
                { opacity: 0, x: -50 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.5,
                    ease: 'power3.out',
                }
            );
        }
    }, []);

    // Final score evenement généré par demande du client
    const emitFinalScoreEvent = (score: number) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'quiz_completed',
            quizCategory: 'Quiz',
            quizAction: 'Completed',
            quizLabel: 'Final Score',
            quizValue: score,
        });

        // console.log('Score envoyé à Google Tag Manager:', score);
    };

    useEffect(() => {
        emitFinalScoreEvent(totalPoints);
    }, [totalPoints]);

    let paragraph = '';
    let paragraph2 = '';

    // Vérifiez si le jeu a commencé et démarrez-le si ce n'est pas le cas
    const { isGameStarted, toggleGame } = useInTheGame();
    if (isGameStarted === false) {
        toggleGame(true);
    }

    // Vérification si le mode est solo ou multi et affichage du texte correspondant
    if (totalPlayers === 1) {
        if (id === 'entrainement') {
            paragraph = `Bravo, vous avez répondu aux ${totalQuestions} questions.`;
            paragraph2 = 'Votre score : ';
        } else {
            paragraph = 'Vous avez atteint';
        }
    } else {
        paragraph = `Bravo à l’équipe **${equipesNoms[winner]}** qui a remporté la victoire !`;
    }

    // Animation de retour
    const handleNewGameClick = () => {
        setGameFinished(false);
        navigate(`/${id}`);
    };

    // Créez une fonction pour récupérer les données des équipes sous forme d'objets avec leur nom et leurs points
    const getEquipesData = () => {
        return equipesNoms.map((nom, index) => ({
            nom,
            points: equipesPoints[index],
        }));
    };

    // Triez les objets par ordre décroissant des points
    const sortedEquipesData = getEquipesData().sort((a, b) => b.points - a.points);

    return (
        <div className='finContainer'>
            <Banner color={`${id === 'expert' ? 'red' : 'blue'}`}>
                <h1 className='banner--mode'>{title ?? ''}</h1>
            </Banner>

            <div className='modeTexte flexible-spacer'>
                <p className='text-special text-special--whiteBig  finContainer__text--upper'>
                    Partie terminée !
                </p>
                <div className='text-page-semib finContainer__text'>
                    <ReactMarkdown
                        components={{
                            p: ({ ...props }) => <p {...props} />,
                        }}
                    >
                        {paragraph}
                    </ReactMarkdown>
                </div>

                {id === 'entrainement' && (
                    <p className='text-page-semib finText--spacer'>{paragraph2}</p>
                )}
                {totalPlayers === 1 && (
                    <h2 className='text-page-semib finTotalPoints'>{`${totalPoints} point${
                        totalPoints > 1 ? 's' : ''
                    }`}</h2>
                )}
                {totalPlayers > 1 && (
                    <div className='finContainer__results'>
                        <ul className='finContainer__iconsList'>
                            {sortedEquipesData.map((equipe, index) => (
                                <li
                                    key={`{${equipe}${index}}`}
                                    className='finContainer__text__listItem'
                                >
                                    {index === 0 ? (
                                        <figure
                                            className={`jeuLayout__icon finContainer__winnerIcon winner`}
                                            key={index}
                                        >
                                            <img
                                                src={`./equipes/white/${equipe.nom}.svg`}
                                                alt={`Equipe ${equipe.nom}`}
                                            />
                                        </figure>
                                    ) : (
                                        <figure
                                            className={`jeuLayout__icon jeuLayout__icon--size`}
                                            key={index}
                                        >
                                            <img
                                                src={`./equipes/blue/${equipe.nom}.svg`}
                                                alt={`Equipe ${equipe}`}
                                            />
                                        </figure>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <ul className='finContainer__text__list'>
                            {
                                /*  totalPlayers > 1 && */

                                sortedEquipesData.map((equipe, index) => (
                                    <li
                                        key={index}
                                        className='finContainer__text__listItem'
                                    >
                                        {index === 0 ? (
                                            <div className='finContainer__winner winner'>
                                                <span className='text-page-bold finContainer__text--winner'>
                                                    Équipe {equipe.nom}
                                                </span>
                                                <span className='text-page-bold finContainer__text--winner'>
                                                    {equipe.points} pts
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className='finContainer__iconsList'>
                                                    <span className='text-page-bold'>
                                                        Équipe {equipe.nom}
                                                    </span>
                                                </div>
                                                <span className='text-page-bold'>
                                                    {equipe.points} pts
                                                </span>
                                            </>
                                        )}
                                    </li>
                                ))
                            }
                            {/*     {totalPlayers < 5 &&
                Array.from({ length: 4 - totalPlayers }).map((_, i) => (
                  <li key={i} className="finContainer__text__listItem">
                    <div className="finContainer__iconsList">
                      <span className="text-page-bold">-</span>
                    </div>
                  </li>
                ))} */}
                        </ul>
                    </div>
                )}
            </div>
            <div className='finButtons'>
                <Button
                    scene='menu'
                    colorName='red'
                    isSelected={false}
                    onClick={handleNewGameClick}
                >
                    Nouvelle partie
                </Button>

                <BackToModesButton
                    pageSelector={'.finContainer'}
                    children={'Retour aux modes de jeu'}
                />
            </div>
        </div>
    );
}

export default Fin;
